/**
 * Stone Investment Filter Panel
 * 
 * Elegant filter panel with Stone Investment's luxury portfolio aesthetic:
 * - Greyscale colour palette
 * - Cinematic accordion transitions
 * - Editorial typography
 */

'use client';

import { useState } from 'react';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
  icon?: React.ReactNode;
}

export interface FilterSection {
  id: string;
  label: string;
  type: 'checkbox' | 'radio' | 'range' | 'price-range';
  options?: FilterOption[];
  min?: number;
  max?: number;
  step?: number;
  defaultExpanded?: boolean;
}

export interface StoneFilterPanelProps {
  /**
   * Filter sections configuration
   */
  sections: FilterSection[];
  /**
   * Current filter values
   */
  values: Record<string, string | string[] | number | { min?: number; max?: number }>;
  /**
   * Callback when filters change
   */
  onChange: (values: Record<string, string | string[] | number | { min?: number; max?: number }>) => void;
  /**
   * Labels for translations
   */
  labels?: {
    title?: string;
    clearAll?: string;
    apply?: string;
    showResults?: string;
    activeFilter?: string;
    activeFilters?: string;
    property?: string;
    properties?: string;
    min?: string;
    max?: string;
  };
  /**
   * Results count
   */
  resultsCount?: number;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneFilterPanel - Elegant filter panel with Stone Investment styling
 */
export function StoneFilterPanel({
  sections,
  values,
  onChange,
  labels = {
    title: 'Refine',
    clearAll: 'Clear all',
    apply: 'Apply',
    showResults: 'Show {{count}} results',
    activeFilter: 'active filter',
    activeFilters: 'active filters',
    property: 'property',
    properties: 'properties',
    min: 'Min',
    max: 'Max',
  },
  resultsCount,
  className = '',
}: StoneFilterPanelProps) {
  const isSingleSection = sections.length === 1;
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(sections.filter(s => s.defaultExpanded !== false || isSingleSection).map(s => s.id))
  );
  
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };
  
  const handleCheckboxChange = (sectionId: string, optionValue: string) => {
    const currentValue = values[sectionId];
    const currentArray = Array.isArray(currentValue) ? currentValue : [];
    
    const newArray = currentArray.includes(optionValue)
      ? currentArray.filter(v => v !== optionValue)
      : [...currentArray, optionValue];
    
    if (newArray.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sectionId]: _, ...rest } = values;
      onChange(rest);
    } else {
      onChange({
        ...values,
        [sectionId]: newArray,
      });
    }
  };
  
  const handleRadioChange = (sectionId: string, optionValue: string) => {
    onChange({
      ...values,
      [sectionId]: optionValue,
    });
  };
  
  const handleRangeChange = (sectionId: string, field: 'min' | 'max', value: number) => {
    const currentValue = values[sectionId] as { min?: number; max?: number } || {};
    
    const newRange = { ...currentValue };
    if (isNaN(value)) {
      delete newRange[field];
    } else {
      newRange[field] = value;
    }

    if (newRange.min === undefined && newRange.max === undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sectionId]: _, ...rest } = values;
      onChange(rest);
    } else {
      onChange({
        ...values,
        [sectionId]: newRange,
      });
    }
  };
  
  const handleClearAll = () => {
    onChange({});
  };
  
  const hasActiveFilters = Object.keys(values).length > 0;
  const activeFilterCount = Object.values(values).filter(v => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v !== '';
    if (typeof v === 'object' && v !== null) {
      const rangeValue = v as { min?: number; max?: number };
      return rangeValue.min !== undefined || rangeValue.max !== undefined;
    }
    return false;
  }).length;
  
  return (
    <div 
      className={`bg-white border border-stone-200 overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-stone-200 bg-stone-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-stone-300 flex items-center justify-center">
              <svg className="w-5 h-5 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <div>
              <h3 
                className="text-lg font-serif text-stone-900"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {labels.title}
              </h3>
              {activeFilterCount > 0 && (
                <p className="text-xs text-stone-500 font-sans tracking-wide">
                  {activeFilterCount} {activeFilterCount === 1 ? (labels.activeFilter || 'active filter') : (labels.activeFilters || 'active filters')}
                </p>
              )}
            </div>
          </div>
          {hasActiveFilters && (
            <button
              onClick={handleClearAll}
              className="text-xs text-stone-600 hover:text-stone-900 font-sans tracking-wide uppercase px-3 py-1.5 border border-stone-300 hover:border-stone-400 transition-colors"
            >
              {labels.clearAll}
            </button>
          )}
        </div>
      </div>
      
      {/* Filter Sections */}
      <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
        {sections.map((section) => {
          const isExpanded = expandedSections.has(section.id);
          const hasActiveValues = values[section.id] !== undefined && (
            (typeof values[section.id] === 'string' && values[section.id] !== '') ||
            (Array.isArray(values[section.id]) && (values[section.id] as string[]).length > 0) ||
            (typeof values[section.id] === 'object' && values[section.id] !== null && !Array.isArray(values[section.id]))
          );
          
          return (
            <div 
              key={section.id} 
              className="border-b border-stone-100 last:border-b-0"
            >
              {/* Section Header */}
              {!isSingleSection && (
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-stone-50 transition-colors text-left group"
                >
                  <div className="flex items-center gap-3">
                    <span 
                      className={`font-sans text-sm uppercase tracking-[0.15em] transition-colors ${hasActiveValues ? 'text-stone-900 font-medium' : 'text-stone-600'}`}
                    >
                      {section.label}
                    </span>
                    {hasActiveValues && (
                      <span className="w-1.5 h-1.5 bg-stone-900" />
                    )}
                  </div>
                  <svg
                    className={`w-4 h-4 text-stone-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
              
              {/* Section Content */}
              {(isExpanded || isSingleSection) && (
                <div className={`px-6 pb-5 space-y-2 ${isSingleSection ? 'pt-4' : ''}`}>
                  {section.type === 'checkbox' && section.options && (
                    <>
                      {section.options.map((option) => {
                        const isChecked = Array.isArray(values[section.id]) && 
                          (values[section.id] as string[]).includes(option.value);
                        
                        return (
                          <label
                            key={option.value}
                            className="flex items-center gap-3 cursor-pointer group py-2 min-h-[44px]"
                          >
                            <div className="relative flex items-center justify-center min-w-[44px] min-h-[44px]">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleCheckboxChange(section.id, option.value)}
                                className="peer w-5 h-5 border border-stone-300 focus:ring-1 focus:ring-stone-400 cursor-pointer transition-all hover:border-stone-400 accent-stone-900"
                              />
                            </div>
                            <span className="flex-1 text-sm text-stone-700 group-hover:text-stone-900 font-sans transition-colors">
                              {option.label}
                            </span>
                            {option.count !== undefined && (
                              <span className="text-xs text-stone-400 font-sans">
                                {option.count}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </>
                  )}
                  
                  {section.type === 'radio' && section.options && (
                    <>
                      {section.options.map((option, optIdx) => {
                        const sectionValue = values[section.id];
                        const isChecked = sectionValue === option.value || 
                          (sectionValue === undefined && optIdx === 0);
                        
                        return (
                          <label
                            key={option.value}
                            className="flex items-center gap-3 cursor-pointer group py-2 min-h-[44px]"
                          >
                            <div className="relative flex items-center justify-center min-w-[44px] min-h-[44px]">
                              <input
                                type="radio"
                                checked={isChecked}
                                onChange={() => handleRadioChange(section.id, option.value)}
                                className="w-5 h-5 border border-stone-300 focus:ring-1 focus:ring-stone-400 cursor-pointer transition-all hover:border-stone-400 accent-stone-900"
                              />
                            </div>
                            <span className="flex-1 text-sm text-stone-700 group-hover:text-stone-900 font-sans transition-colors">
                              {option.label}
                            </span>
                            {option.count !== undefined && (
                              <span className="text-xs text-stone-400 font-sans">
                                {option.count}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </>
                  )}
                  
                  {(section.type === 'range' || section.type === 'price-range') && (
                    <div className="space-y-4 pt-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-sans uppercase tracking-[0.2em] text-stone-500 mb-2">
                            {labels.min}
                          </label>
                          <input
                            type="number"
                            min={section.min}
                            max={section.max}
                            step={section.step || 1}
                            value={(values[section.id] as { min?: number })?.min ?? ''}
                            onChange={(e) => handleRangeChange(section.id, 'min', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-stone-300 text-sm font-sans focus:ring-1 focus:ring-stone-400 focus:border-stone-400 transition-all hover:border-stone-400"
                            placeholder={section.min?.toString()}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-sans uppercase tracking-[0.2em] text-stone-500 mb-2">
                            {labels.max}
                          </label>
                          <input
                            type="number"
                            min={section.min}
                            max={section.max}
                            step={section.step || 1}
                            value={(values[section.id] as { max?: number })?.max ?? ''}
                            onChange={(e) => handleRangeChange(section.id, 'max', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-stone-300 text-sm font-sans focus:ring-1 focus:ring-stone-400 focus:border-stone-400 transition-all hover:border-stone-400"
                            placeholder={section.max?.toString()}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Results Summary Footer */}
      {resultsCount !== undefined && (
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-serif text-2xl text-stone-900">{resultsCount}</span>
            <span className="text-xs font-sans uppercase tracking-[0.15em] text-stone-500">
              {resultsCount === 1 ? (labels.property || 'property') : (labels.properties || 'properties')}
            </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StoneFilterPanel;
